// components/DevSubscriptionTools.tsx
// ─────────────────────────────────────────────────────────────
// Dev-only floating panel for testing the subscription/paywall
// flow without waiting 14 days.
//
// ONLY renders when import.meta.env.DEV is true (Vite local dev).
// Automatically excluded from production builds.
// ─────────────────────────────────────────────────────────────

import { useState } from "react";
import { useSubscription } from "../context/SubscriptionContext";
import { supabase } from "../lib/supabase";
import { useAuth } from "../context/AuthContext";

export default function DevSubscriptionTools() {
  // Never render in production
  if (!import.meta.env.DEV) return null;

  return <DevPanel />;
}

function DevPanel() {
  const { profile } = useAuth();
  const {
    status, daysLeft, isLocked, currentPlan, plans,
    simulateExpiry, resetSimulation, refetch,
  } = useSubscription();

  const [open,    setOpen]    = useState(false);
  const [working, setWorking] = useState(false);

  // ── Helpers ─────────────────────────────────────────────────
  const setTrialEndsIn = async (minutes: number) => {
    if (!profile?.id) return;
    setWorking(true);
    const newEnd = new Date(Date.now() + minutes * 60 * 1000).toISOString();
    await supabase
      .from("subscriptions")
      .update({ status: "trialing", trial_ends_at: newEnd, current_period_end: null })
      .eq("owner_id", profile.id);
    await refetch();
    setWorking(false);
  };

  const setExpired = async () => {
    if (!profile?.id) return;
    setWorking(true);
    resetSimulation();
    await supabase
      .from("subscriptions")
      .update({ status: "expired", trial_ends_at: new Date(Date.now() - 1000).toISOString() })
      .eq("owner_id", profile.id);
    await refetch();
    setWorking(false);
  };

  const setPlan = async (planId: string) => {
    if (!profile?.id) return;
    setWorking(true);
    resetSimulation();
    const plan = plans.find(p => p.id === planId);
    await supabase
      .from("subscriptions")
      .update({
        status: "active",
        plan: planId,
        price: plan?.price ?? 0,
        current_period_start: new Date().toISOString(),
        current_period_end: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
      })
      .eq("owner_id", profile.id);
    await refetch();
    setWorking(false);
  };

  const resetToFullTrial = async () => {
    if (!profile?.id) return;
    setWorking(true);
    resetSimulation();
    await supabase
      .from("subscriptions")
      .update({
        status: "trialing",
        plan: null,
        trial_ends_at: new Date(Date.now() + 21 * 24 * 60 * 60 * 1000).toISOString(),
        current_period_end: null,
      })
      .eq("owner_id", profile.id);
    await refetch();
    setWorking(false);
  };

  // Status colour
  const statusColor = status === "trialing" ? "#fbbf24" : status === "active" ? "#34d399" : status === "expired" ? "#f87171" : "#9ca3af";

  return (
    <div style={{ position: "fixed", bottom: 16, right: 16, zIndex: 9999, fontFamily: "DM Mono, monospace" }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Toggle button */}
      <button
        onClick={() => setOpen(o => !o)}
        title="Dev: Subscription Tools"
        style={{
          background: "#1a1a2e",
          border: "1px solid #4ade80",
          borderRadius: 10,
          padding: "7px 12px",
          color: "#4ade80",
          fontSize: 11,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 6,
          boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
          fontFamily: "DM Mono, monospace",
        }}
      >
        <span style={{ fontSize: 13 }}>🧪</span>
        DEV
        <span style={{ width: 7, height: 7, borderRadius: "50%", background: statusColor, flexShrink: 0 }} />
      </button>

      {/* Panel */}
      {open && (
        <div style={{
          position: "absolute",
          bottom: 44,
          right: 0,
          width: 280,
          background: "#0d1117",
          border: "1px solid #4ade8040",
          borderRadius: 14,
          padding: "16px",
          boxShadow: "0 8px 40px rgba(0,0,0,0.7)",
        }}>
          {/* Header */}
          <div style={{ fontSize: 10, color: "#4ade80", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>
            🧪 Subscription Dev Tools
          </div>

          {/* Current status */}
          <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.06)", borderRadius: 8, padding: "10px 12px", marginBottom: 14, fontSize: 11 }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ color: "rgba(255,255,255,0.4)" }}>Status</span>
              <span style={{ color: statusColor, fontWeight: 600, textTransform: "uppercase" }}>{status}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ color: "rgba(255,255,255,0.4)" }}>Days left</span>
              <span style={{ color: "#f9fafb" }}>{daysLeft}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
              <span style={{ color: "rgba(255,255,255,0.4)" }}>Plan</span>
              <span style={{ color: "#f9fafb" }}>{currentPlan?.name ?? "None"}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "rgba(255,255,255,0.4)" }}>Locked</span>
              <span style={{ color: isLocked ? "#f87171" : "#34d399" }}>{isLocked ? "Yes" : "No"}</span>
            </div>
          </div>

          {/* Quick actions */}
          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
            Trial
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
            <DevBtn onClick={() => setTrialEndsIn(2)} disabled={working} color="#fbbf24">
              Trial expires in 2 minutes
            </DevBtn>
            <DevBtn onClick={() => setTrialEndsIn(60)} disabled={working} color="#fbbf24">
              Trial expires in 1 hour
            </DevBtn>
            <DevBtn onClick={setExpired} disabled={working} color="#f87171">
              Expire trial now (show paywall)
            </DevBtn>
            <DevBtn onClick={() => simulateExpiry()} disabled={working} color="#f87171">
              Simulate expired (client only)
            </DevBtn>
          </div>

          <div style={{ fontSize: 10, color: "rgba(255,255,255,0.3)", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 8 }}>
            Activate Plan (skip payment)
          </div>
          <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 14 }}>
            {plans.map(plan => (
              <DevBtn key={plan.id} onClick={() => setPlan(plan.id)} disabled={working} color="#06b6d4">
                Activate {plan.name} (KSh {plan.price.toLocaleString()})
              </DevBtn>
            ))}
          </div>

          <DevBtn onClick={resetToFullTrial} disabled={working} color="#34d399">
            ↺ Reset to full 21-day trial
          </DevBtn>

          {working && (
            <div style={{ textAlign: "center", marginTop: 10, color: "rgba(255,255,255,0.3)", fontSize: 11, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              <span style={{ width: 10, height: 10, border: "2px solid rgba(255,255,255,0.1)", borderTopColor: "#4ade80", borderRadius: "50%", display: "inline-block", animation: "spin 0.7s linear infinite" }} />
              Updating…
            </div>
          )}

          <div style={{ marginTop: 12, fontSize: 10, color: "rgba(255,255,255,0.2)", textAlign: "center", lineHeight: 1.5 }}>
            Only visible in dev mode.<br />Invisible in production builds.
          </div>
        </div>
      )}
    </div>
  );
}

function DevBtn({ onClick, disabled, color, children }: {
  onClick: () => void;
  disabled: boolean;
  color: string;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      style={{
        width: "100%",
        background: `${color}10`,
        border: `1px solid ${color}30`,
        borderRadius: 7,
        padding: "7px 10px",
        color,
        fontSize: 11,
        fontFamily: "DM Mono, monospace",
        cursor: disabled ? "not-allowed" : "pointer",
        textAlign: "left",
        opacity: disabled ? 0.5 : 1,
      }}
    >
      {children}
    </button>
  );
}
